import { alpha, Box, Button, Chip, FormControl, Stack, Tooltip } from '@mui/material';
import React, { lazy, useEffect, useRef, useState } from 'react';
import ReactSelect from 'components/common/react-select/ReactSelect';
import { FormComponentValue } from 'components/common/form/layout/control';
import { AlertRuleScreenProps, alertSimulationWidget } from 'components/alert-rule-popup/index';
import { Trans, useTranslation } from 'react-i18next';
import { prepareFormElementProps } from 'components/common/form/formTools';
import ReactHookFormController from 'components/common/form/layout/ReactHookFormController';
import { FormRendererAPIType } from 'components/common/form';
import useCustomSimplifiedForm from 'components/common/form/hooks/useCustomSimplifiedForm';
import IconMi from 'components/common/icon/IconMi';
import Alert from '@mui/material/Alert';
import LoadingPlaceholder from 'components/common/loading-placeholder/LoadingPlaceholder';
import { alertRuleAPI } from 'api/alert-wizard';
import MITable from 'components/common/grid/MITable';
import FormText from 'components/common/form/layout/control/FormText';
import { AlertRulePrepareFormData } from 'components/alert-rule-popup/AlertRuleDataProcessor';
import filterOnKeyDown from 'tools/filterInputValue';
const AlertRuleChart = lazy(() => import('components/alert-rule-popup/AlertRuleChart'));

interface AlertRuleSimulationControlProps extends AlertRuleScreenProps {
    onChange?: (text: any) => void;
}

const getFullRange = (unit: string, int: number) => {
    switch (unit) {
        case 'hour':
            unit = 'HOUR';
            break;
        case 'day':
            unit = 'DAY';
            break;
        case 'week':
            unit = 'DAY';
            int *= 7;
            break;
        case 'month':
            unit = 'DAY';
            int *= 30;
            break;
        case 'quarter':
            unit = 'DAY';
            int *= 3 * 30;
            break;
        case 'year':
            unit = 'DAY';
            int *= 365;
            break;
    }

    return int + ' ' + unit;
};

export function subtractRangeFromDate(unit: string, int: number, startDate: Date, onePoint?: any) {
    const d = getFullRange(unit, int);
    let tmp = d.split(' ');
    const pointVal = onePoint === true ? 1 : parseInt(tmp[0]);

    switch (tmp[1]) {
        case 'HOUR':
            startDate.setHours(startDate.getHours() - pointVal);
            break;
        case 'DAY':
            startDate.setDate(startDate.getDate() - pointVal);
            break;
        case 'week':
            startDate.setDate(startDate.getDate() - 7 * pointVal);
            break;
        case 'MONTH':
            startDate.setMonth(startDate.getMonth() - pointVal);
            break;
        case 'quarter':
            startDate.setMonth(startDate.getMonth() - 3 * pointVal);
            break;
        case 'YEAR':
            startDate.setFullYear(startDate.getFullYear() - pointVal);
            break;
    }
    return startDate;
}

export default function AlertRuleSimulationControl(props: AlertRuleSimulationControlProps) {
    const { alertRuleData, wizardData, updateWizardData = () => {} } = props;

    const { t } = useTranslation(['components/alert-rule-popup/alert_rule_popup']);

    const [alertSimulationWidget, setAlertSimulationWidget] = useState<alertSimulationWidget>({
        active: false,
        lastSimulationFormData: '',
        actualSimulationId: 0,
        chartData: {},
        disabled: true,
    });

    //workaround for settimeout get state
    const alertSimulationWidgetRef = useRef(alertSimulationWidget);

    const updateAlertSimulationWidget = (newData: any) => {
        alertSimulationWidgetRef.current = { ...alertSimulationWidgetRef.current, ...newData };
        setAlertSimulationWidget((prevAlertSimulationWidgetData) => ({ ...prevAlertSimulationWidgetData, ...newData }));
    };

    const [alertSimulationNumber, setAlertSimulationNumber] = useState<string>('30');
    const [simulationRangeVal, setSimulationRangeVal] = useState<string>('');
    const [simulationSegmentVal, setSimulationSegmentVal] = useState<string>('0');

    const [showSimulationNoDataMsg, setShowSimulationNoDataMsg] = useState<boolean>(false);
    const [simDim, setSimDim] = useState<number>(0);
    const [simEvent, setSimEvent] = useState<number>(0);

    const [showSimulationGrid, setShowSimulationGrid] = useState<boolean>(false);
    const [showSimulationPreview, setShowSimulationPreview] = useState<boolean>(false);
    const [simulationChartPreviewData, setSimulationChartPreviewData] = useState<any>();

    const select_simulation_range: FormComponentValue[] = Object.keys(wizardData.simulation.intervals).map(
        (optionKey: string) => {
            return {
                label: t(`form.criteria.field_value.mi_intervals.` + optionKey, { count: 2 }),
                value: optionKey,
            };
        },
    );

    const select_segment_values: FormComponentValue[] = wizardData.simulation.segmentValues.map((option: any) => {
        return {
            label: option.value_display_name,
            value: option.segment_value_id,
        };
    });

    useEffect(() => {
        updateAlertSimulationWidget({
            disabled: !wizardData.dashboardElement.enabled_ind || wizardData.dashboardElement.enabled_ind === 'N',
        });

        setSimulationRangeVal(select_simulation_range[0].value);

        const selectedSegment = wizardData.simulation.segmentValues.find((item: any) => {
            return item.segment_value_id == wizardData.simulation.segmentValue;
        });

        if (selectedSegment) {
            setSimulationSegmentVal(selectedSegment.segment_value_id);
        } else {
            if (select_segment_values.length) {
                setSimulationSegmentVal(select_segment_values[0].value);
            }
        }
    }, []);

    const cancelSimulation = () => {
        alertRuleAPI
            .cancelSimulation(alertSimulationWidgetRef.current.actualSimulationId)
            .then((response) => {})
            .catch(() => {});
        updateAlertSimulationWidget({ active: false });
    };

    const maskBlock = (
        <LoadingPlaceholder
            relativeToParent
            sx={{
                zIndex: 5,
                boxShadow: (theme) => `0px 8px 32px 0px ${alpha(theme.palette.text.primary, 0.4)}`,
                '.loading-placeholder__icon': {
                    display: 'none',
                },
                '.loading-placeholder__container': {
                    color: 'text.primary',
                    textAlign: 'center',
                    backgroundColor: (theme) => theme.palette.background.default,
                    borderRadius: 1,
                },
            }}
        >
            <Box sx={{ width: '408px', mt: '0 !important' }}>
                {Number(simulationSegmentVal) != -1 && (
                    <Box sx={{ pt: 3 }}>
                        <Box component={'strong'} sx={{ fontSize: '16px' }}>
                            {t('form.simulation.loading_label')}
                        </Box>
                    </Box>
                )}
                {Number(simulationSegmentVal) == -1 && (
                    <Box>
                        <Box
                            sx={{
                                height: '6px',
                                backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
                                overflow: 'hidden',
                                mb: 3,
                            }}
                        >
                            <Box
                                className={'dSimDimBar'}
                                sx={{
                                    backgroundColor: 'primary.main',
                                    height: '100%',
                                    width: (simDim / (select_segment_values.length - 1)) * 100 + '%',
                                }}
                            />
                        </Box>
                        <Box component={'strong'} sx={{ fontSize: '16px' }}>
                            {t('form.simulation.loading_label')}
                        </Box>
                        <Box sx={{ pt: 2 }}>
                            <Box component={'span'} className={'dSimDim'}>
                                {simDim}
                            </Box>
                            /{select_segment_values.length - 1} {t('form.simulation.loading_dimension_values')}
                        </Box>
                        <Box
                            sx={{
                                display: 'inline-block',
                                mt: 2,
                            }}
                        >
                            <Chip
                                label={`${simEvent} ${t('form.simulation.loading_alert_events')}`}
                                color="primary"
                                className={'dSimEvent'}
                            />
                        </Box>
                    </Box>
                )}
                <Box
                    sx={{
                        p: 2,
                        display: 'flex',
                        justifyContent: 'center',
                        borderTop: '1px solid',
                        borderTopColor: (theme) => alpha(theme.palette.text.primary, 0.08),
                        mt: 3,
                    }}
                >
                    <Button
                        className="dSimCancel"
                        onClick={(e) => {
                            cancelSimulation();
                        }}
                    >
                        {t('form.simulation.loading_cancel')}
                    </Button>
                </Box>
            </Box>
        </LoadingPlaceholder>
    );

    const manageView = async (eventAmount: number) => {
        let segmentName: any = '';
        if (simulationSegmentVal && Number(simulationSegmentVal) != -1) {
            segmentName = select_segment_values.find((item) => {
                return item.value === simulationSegmentVal;
            })?.label;
        }

        if (Number(simulationSegmentVal) != 0) {
            updateAlertSimulationWidget({
                chartData: { ...alertSimulationWidgetRef.current.chartData, segment_value_id: simulationSegmentVal },
            });
        } else {
            const newData = { ...{}, ...alertSimulationWidgetRef.current.chartData };
            delete newData.segment_value_id;
            updateAlertSimulationWidget({ chartData: newData });
        }

        setShowSimulationNoDataMsg(false);
        updateWizardData({
            simulationSegmentName: segmentName ?? '',
            showAlertSimulationText: true,
            simulationTriggerDate: '',
            simulationTriggerCount: '',
        });

        if (Number(simulationSegmentVal) == -1) {
            await alertRuleAPI
                .getSimulationGridData(alertSimulationWidgetRef.current.actualSimulationId)
                .then((response) => {
                    if (response.status == 200) {
                        updateWizardData({ simulationGrid: response.data });
                    } else {
                        updateWizardData({ simulationGrid: [] });
                    }
                })
                .catch(() => {});
            setShowSimulationGrid(true);
            setShowSimulationPreview(false);
        } else {
            //alertWizard.initElementPreview($('#alertElementSimulationPreview'),
            setSimulationChartPreviewData({
                elementId: alertSimulationWidgetRef.current.chartData.element_id,
                segmentValueId: alertSimulationWidgetRef.current.chartData.segment_value_id,
                options: {
                    startDate: subtractRangeFromDate(simulationRangeVal, parseInt(alertSimulationNumber), new Date()),
                    simulation_id: alertSimulationWidgetRef.current.actualSimulationId,
                    unitToSimulate: simulationRangeVal,
                    intToSimulate: parseInt(alertSimulationNumber),
                },
            });
            setShowSimulationGrid(false);
            setShowSimulationPreview(true);
        }

        updateWizardData({
            simulationTriggerCount: t('form.simulation.trigger_count', { count: eventAmount }),
        });

        updateWizardData({
            simulationTriggerDate: t('form.simulation.trigger_date', {
                count: parseInt(alertSimulationNumber),
                interval: t('form.criteria.field_value.mi_intervals.' + simulationRangeVal, { count: 1 }),
            }),
        });
    };

    const checker = () => {
        setTimeout(function () {
            alertRuleAPI
                .checkSimulationStatus(alertSimulationWidgetRef.current.actualSimulationId)
                .then((response) => {
                    if (alertSimulationWidgetRef.current.active) {
                        const responseData = response.data.simulation;
                        setSimDim(responseData.segments);
                        setSimEvent(responseData.events);

                        if (responseData.status == 'done') {
                            updateAlertSimulationWidget({ active: false });
                            manageView(responseData.events);
                        } else {
                            checker();
                        }
                    } else {
                        updateAlertSimulationWidget({ active: false });
                    }
                })
                .catch(() => {});
        }, 800);
    };

    const createNewSimulation = async () => {
        updateAlertSimulationWidget({ lastSimulationFormData: JSON.stringify(alertRuleData) });
        const params = {
            ...AlertRulePrepareFormData(alertRuleData, wizardData),
            simulation_sv_id: simulationSegmentVal,
            simulation_range: getFullRange(simulationRangeVal, parseInt(alertSimulationNumber)),
            user_defined_name: 'alert_simulation',
        };
        await alertRuleAPI
            .createSimulation(params)
            .then((response) => {
                if (response.data?.status == 'OK') {
                    updateWizardData({ error: [] });
                    updateAlertSimulationWidget({ actualSimulationId: response.data?.id });
                    setTimeout(function () {
                        checker();
                    }, 100);
                } else if (response.data?.status == 'ERROR') {
                    let errorMsg = response.data?.message ?? '';
                    if (typeof response.data?.messages == 'object' && response.data?.messages) {
                        errorMsg = '';
                        console.log(Object.values(response.data.messages));
                        Object.values(response.data.messages).forEach((item: any, index) => {
                            errorMsg += item.error;
                            if (index) errorMsg += '\n';
                        });
                    }
                    updateWizardData({ error: [errorMsg] });
                    updateAlertSimulationWidget({ active: false });
                }
            })
            .catch(() => {});
        //updateAlertSimulationWidget({ active: false }); check it

        return true;
    };

    const runSimulation = async () => {
        setSimDim(0); // reset...
        setSimEvent(0); // reset...
        updateAlertSimulationWidget({
            active: true,
            chartData: { ...{}, ...wizardData.dashboardElement },
        });

        await createNewSimulation();
    };

    const gridColumns = ['dimension_value', 'number_of_alerts'].map((columnKey) => {
        return {
            name: columnKey,
            title: t('form.simulation.grid.' + columnKey),
            resizable: false,
        };
    });

    const gridData = (wizardData.simulationGrid ?? []).map((item: any) => {
        return { dimension_value: item.value_display_name, number_of_alerts: item.amount };
    });

    return (
        <>
            {alertSimulationWidgetRef.current.active && maskBlock}
            <Stack direction={'row'} alignItems={'center'} sx={{ mt: 2 }} spacing={1}>
                <Box flexShrink={0}>{t('form.simulation.label_simulate_based_on')}</Box>

                <Box
                    flexShrink={0}
                    sx={{
                        display: 'inline-flex',
                        width: '30px',
                        input: { px: 0.5, textAlign: 'center' },
                        '.MuiFormLabel-root': { display: 'none' },
                    }}
                >
                    {/*dAlertSimulationNumber*/}
                    {/*ToDo if need then add watch*/}
                    <FormControl>
                        <FormText
                            controlProps={{
                                uid: 'alert-simulation-number',
                                form: {} as FormRendererAPIType,
                                name: 'alert_simulation_number',
                                onChange: (val) => {
                                    setAlertSimulationNumber(val);
                                },
                                onBlur: () => {
                                    const newVal = Number(alertSimulationNumber);
                                    if (!(newVal && newVal > 0)) {
                                        setAlertSimulationNumber('1');
                                    }
                                },
                                onKeyDown: (e) => {
                                    filterOnKeyDown(e, 'integerPositive');
                                },
                                value: alertSimulationNumber,
                            }}
                        />
                    </FormControl>
                </Box>

                <Box sx={{ width: '100%' }}>
                    <ReactSelect
                        dataTest={'alert-simulation-range'}
                        data={select_simulation_range}
                        value={[simulationRangeVal]}
                        update={(val, fullValue) => {
                            setSimulationRangeVal(val);
                        }}
                    />
                </Box>

                {wizardData.simulation.segmentValues?.length > 0 && (
                    <>
                        <Box flexShrink={0}>{t('form.simulation.label_for')}</Box>
                        <Box sx={{ width: '100%' }}>
                            <ReactSelect
                                dataTest={'alert-simulation-segment-values'}
                                data={select_segment_values}
                                value={[simulationSegmentVal]}
                                update={(val, fullValue) => {
                                    setSimulationSegmentVal(val);
                                }}
                            />
                        </Box>
                    </>
                )}
                <Box flexShrink={0}>
                    <Tooltip title={alertSimulationWidgetRef.current.disabled ? t('form.simulation.btn_tooltip') : ''}>
                        <Box component={'span'}>
                            <Button
                                data-test={'alert-simulation-button'}
                                className={'dAlertSimulation'}
                                startIcon={<IconMi icon={'check'} />}
                                onClick={runSimulation}
                                disabled={
                                    alertSimulationWidgetRef.current.disabled ||
                                    (alertRuleData.alert_type == 'compound' &&
                                        alertRuleData.compound_selected_alert_rule.length == 0)
                                }
                            >
                                {t('form.simulation.button')}
                            </Button>
                        </Box>
                    </Tooltip>
                </Box>
            </Stack>
            {alertSimulationWidgetRef.current.disabled && (
                <Alert severity={'info'} variant={'standard'} sx={{ mt: 1 }}>
                    <Trans
                        t={t}
                        i18nKey="form.simulation.disabled_error_msg"
                        values={{
                            name: wizardData.dashboardElement?.raw_name ?? '',
                        }}
                    >
                        <Box component="strong">placeholder_bold_text</Box>
                        placeholder_text_after
                    </Trans>
                </Alert>
            )}

            {showSimulationNoDataMsg && (
                <Alert severity={'info'} variant={'standard'}>
                    <Box id={'alertSimulationNoData'}>{t('form.simulation.chart_has_no_data')}</Box>
                </Alert>
            )}

            {showSimulationGrid && (
                <Box id={'alertElementSimulationGrid'} sx={{ mt: 2 }}>
                    {gridData?.length > 0 ? (
                        <MITable
                            data={gridData}
                            height={150}
                            columns={gridColumns}
                            massActionsConfig={{ enabled: false }}
                            gridName={'alert_rule_simulation'}
                        />
                    ) : (
                        <Box
                            sx={{
                                border: '1px solid',
                                borderColor: (theme) => alpha(theme.palette.text.primary, 0.08),
                                mt: 2,
                                padding: 1.5,
                                textAlign: 'center',
                            }}
                        >
                            {t('form.simulation.grid_has_no_data')}
                        </Box>
                    )}
                </Box>
            )}
            {/* alertWizard.initElementPreview($('#alertElementSimulationPreview'), */}
            {showSimulationPreview && simulationChartPreviewData && (
                <Box id={'alertElementSimulationPreview'} sx={{ mt: 2 }}>
                    <AlertRuleChart {...simulationChartPreviewData} />
                </Box>
            )}
        </>
    );
}
